import { Temporal } from 'temporal-polyfill'
import type { TimeZoneDto } from '@/types/lookups'

/**
 * A single selectable timezone in the combobox: the IANA id is the bound value;
 * `label`/`detail` are for display; `searchText` is the lowercased haystack we filter on.
 */
export interface TimeZoneChoice {
  /** IANA id, e.g. "Europe/London" — the value bound to the form field. */
  id: string
  /** Primary label, e.g. "London" or "Eastern Time". */
  label: string
  /** Secondary detail, e.g. "America · GMT-4". */
  detail: string
  /** Lowercased search haystack (id + city + region + display name + abbreviation + aliases). */
  searchText: string
  /** True for the curated common zones — shown first and on an empty search. */
  isCommon: boolean
}

/**
 * Extra search keywords for zones whose IANA id doesn't contain the words people type
 * ("England" isn't in "Europe/London"). Search-only: typing any alias surfaces the mapped
 * zone. Keep the list small and common — breadth comes from the full IANA set, not this map.
 */
const TIME_ZONE_ALIASES: Record<string, string[]> = {
  'Europe/London': ['england', 'britain', 'uk', 'united kingdom', 'gb', 'gmt', 'bst'],
  'America/New_York': ['eastern', 'et', 'est', 'edt', 'nyc', 'new york city', 'usa east'],
  'America/Chicago': ['central', 'ct', 'cst', 'cdt', 'usa central'],
  'America/Denver': ['mountain', 'mt', 'mst', 'mdt'],
  'America/Phoenix': ['arizona', 'mst'],
  'America/Los_Angeles': ['pacific', 'pt', 'pst', 'pdt', 'california', 'san francisco', 'seattle', 'usa west'],
  'America/Anchorage': ['alaska', 'akst', 'akdt'],
  'Pacific/Honolulu': ['hawaii', 'hst'],
  'America/Mexico_City': ['mexico', 'cst'],
  'America/Sao_Paulo': ['brazil', 'brt'],
  'America/Argentina/Buenos_Aires': ['argentina', 'art'],
  'Europe/Paris': ['france', 'cet', 'cest'],
  'Europe/Berlin': ['germany', 'cet', 'cest'],
  'Europe/Madrid': ['spain'],
  'Europe/Rome': ['italy'],
  'Europe/Athens': ['greece', 'eet'],
  'Europe/Moscow': ['russia', 'msk'],
  'Asia/Dubai': ['uae', 'united arab emirates', 'gst'],
  'Asia/Kolkata': ['india', 'ist', 'mumbai', 'delhi', 'bangalore', 'calcutta'],
  'Asia/Singapore': ['sgt'],
  'Asia/Hong_Kong': ['hkt'],
  'Asia/Tokyo': ['japan', 'jst'],
  'Asia/Shanghai': ['china', 'beijing', 'cst'],
  'Australia/Sydney': ['aedt', 'aest', 'nsw'],
  'Australia/Perth': ['awst', 'western australia'],
  'Pacific/Auckland': ['new zealand', 'nz', 'nzdt', 'nzst'],
  UTC: ['utc', 'gmt', 'zulu', 'universal', 'coordinated'],
}

/** The full IANA zone list from the browser, or `[]` on very old runtimes without the API. */
export function supportedIanaZones(): string[] {
  const withApi = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  try {
    return withApi.supportedValuesOf?.('timeZone') ?? []
  } catch {
    return []
  }
}

/** Split an IANA id into a human city + region, e.g. "America/Argentina/Buenos_Aires". */
export function splitIanaId(id: string): { city: string; region: string } {
  const parts = id.split('/')
  const city = (parts[parts.length - 1] ?? id).replace(/_/g, ' ')
  const region = parts.slice(0, -1).join(' / ').replace(/_/g, ' ')
  return { city, region }
}

/**
 * Constructing an Intl.DateTimeFormat is the expensive part; formatting with one is not. The zone
 * picker builds an option per IANA zone and the meetings list labels a row per meeting on every
 * render, so the formatters are kept and only formatToParts runs per call. `null` marks a zone the
 * runtime rejected, so an unknown id costs one throw rather than one per render.
 */
const zoneFormatters = new Map<string, Intl.DateTimeFormat | null>()

function zoneNamePart(locale: string, id: string, style: 'short' | 'shortOffset', instant: Date): string {
  const key = `${locale}|${style}|${id}`
  let fmt = zoneFormatters.get(key)
  if (fmt === undefined) {
    try {
      fmt = new Intl.DateTimeFormat(locale, { timeZone: id, timeZoneName: style } as Intl.DateTimeFormatOptions)
    } catch {
      fmt = null
    }
    zoneFormatters.set(key, fmt)
  }
  return fmt?.formatToParts(instant).find(part => part.type === 'timeZoneName')?.value ?? ''
}

/** Current short GMT offset for a zone, e.g. "GMT-4"; "" if the runtime can't format it. */
export function shortOffset(id: string, now: Date): string {
  return zoneNamePart('en-US', id, 'shortOffset', now)
}

/**
 * Intl's short zone names are locale-specific: en-US names the American zones (EDT) and en-GB the
 * European ones (BST), and each gives the other's zones a GMT+n offset instead. Both are asked so
 * the whole curated list is covered.
 */
const NAMING_LOCALES = ['en-US', 'en-GB']

/** "GMT+8", "UTC-5:30" — what Intl falls back to when it has no name for a zone in that locale. */
const OFFSET_STYLE = /^(?:GMT|UTC)[+-]/

/** Chromium's ICU writes Greenwich Mean Time as "GMT+0" in both locales: no offset from GMT is GMT. */
const GREENWICH_OFFSET = /^GMT[+-]0(?::?00)?$/

/**
 * The abbreviation a zone goes by at a given instant, e.g. "BST" for Europe/London in July and
 * "GMT" in January (ORB-872). Returns "" where no locale has a name for the zone — Asia/Singapore
 * and Australia/Sydney are offsets to Intl however it is asked — and where the id is unknown.
 */
export function namedAbbreviation(id: string, instant: Date): string {
  let greenwich = false
  for (const locale of NAMING_LOCALES) {
    const name = zoneNamePart(locale, id, 'short', instant)
    if (name && !OFFSET_STYLE.test(name)) return name
    greenwich ||= GREENWICH_OFFSET.test(name)
  }
  return greenwich ? 'GMT' : ''
}

/**
 * The moment an event display timestamp actually names. The API sends an event's start and end as
 * that event's own wall clock labelled `+00:00`, not a true instant (ORB-446), so reading the wire
 * offset literally lands hours away — far enough to sit on the wrong side of a daylight transition
 * and pick the wrong abbreviation. Temporal resolves the wall clock in the zone that owns it.
 * Returns null when the clock is unusable; falls back to the wire value when the zone is.
 *
 * Imported rather than taken off `globalThis`, which only main.tsx populates — a module-level
 * import is the same instance in the browser and the one thing that is also there under vitest.
 */
export function eventZoneInstant(wireValue: string, timeZone: string | null | undefined): Date | null {
  const wallClock = wireValue.replace(/(?:Z|[+-]\d{2}:?\d{2})$/i, '').trim()
  if (timeZone && wallClock) {
    try {
      const zoned = Temporal.PlainDateTime.from(wallClock).toZonedDateTime(timeZone)
      return new Date(zoned.epochMilliseconds)
    } catch {
      // Unrecognised zone or unparseable wall clock — fall through to the wire value.
    }
  }
  const parsed = new Date(wireValue)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * The ~23 curated common zones as a fallback so favorites still render synchronously from the
 * browser IANA set before the API lookup lands (or if it fails). Mirrors GetTimeZones.cs.
 */
const FALLBACK_COMMON_ZONE_IDS = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
  'Europe/Athens', 'Europe/Moscow', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Shanghai',
  'Australia/Sydney', 'Australia/Perth', 'Pacific/Auckland',
  'America/Mexico_City', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'UTC',
]

/**
 * Build the full option universe: curated common zones first (with the API's friendly
 * display names, shown on an empty search), then every other IANA zone the browser knows.
 * The bound value is always the IANA id. `now` seeds the DST-correct offset labels.
 */
export function buildTimeZoneChoices(common: TimeZoneDto[], now: Date): TimeZoneChoice[] {
  const seen = new Set<string>()
  const choices: TimeZoneChoice[] = []

  const push = (id: string, isCommon: boolean, apiDisplayName?: string, abbreviation?: string) => {
    if (seen.has(id)) return
    seen.add(id)
    const { city, region } = splitIanaId(id)
    // The API display name carries an offset prefix, e.g. "(UTC-05:00) Eastern Time" — strip it.
    const cleanApi = apiDisplayName?.replace(/^\(UTC[^)]*\)\s*/, '')
    const offset = shortOffset(id, now)
    const label = cleanApi || city
    const detail = [region, offset].filter(Boolean).join(' · ')
    const aliases = (TIME_ZONE_ALIASES[id] ?? []).join(' ')
    const searchText = `${id} ${city} ${region} ${cleanApi ?? ''} ${abbreviation ?? ''} ${aliases}`.toLowerCase()
    choices.push({ id, label, detail, searchText, isCommon })
  }

  for (const tz of common) push(tz.Id, true, tz.DisplayName, tz.Abbreviation)
  for (const id of FALLBACK_COMMON_ZONE_IDS) push(id, true)
  for (const id of supportedIanaZones()) push(id, false)
  return choices
}

/**
 * Filter + rank choices for a query. Empty query → the common zones only (browse mode).
 * Non-empty → any haystack match, ranked (label-prefix > label-substring, common first),
 * then capped so we never render the whole 400-zone list at once.
 */
export function filterTimeZoneChoices(choices: TimeZoneChoice[], query: string, limit = 50): TimeZoneChoice[] {
  const q = query.trim().toLowerCase()
  if (q === '') return choices.filter(c => c.isCommon)

  return choices
    .filter(c => c.searchText.includes(q))
    .map(c => {
      const label = c.label.toLowerCase()
      let score = 0
      if (label.startsWith(q)) score -= 3
      else if (label.includes(q)) score -= 1
      if (c.isCommon) score -= 1
      return { c, score }
    })
    .sort((a, b) => a.score - b.score || a.c.label.localeCompare(b.c.label))
    .slice(0, limit)
    .map(s => s.c)
}
